import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Article as ArticleIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  PictureAsPdf as PdfIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import type { SentGuestCard, SentListing } from "./types";
import type { Invoice } from "./invoiceTypes";
import { formatDate } from "./helpers";
import { exportInvoicesCsv } from "../../services/invoiceService";

type FileItem =
  | {
      id: string;
      kind: "sent_list";
      label: string;
      detail: string;
      recipient?: string;
      createdAt: string;
      source: SentListing;
    }
  | {
      id: string;
      kind: "guest_card";
      label: string;
      detail: string;
      recipient?: string;
      createdAt: string;
      source: SentGuestCard;
    }
  | {
      id: string;
      kind: "invoice";
      label: string;
      detail: string;
      recipient?: string;
      createdAt: string;
      source: Invoice;
    };

interface ClientFilesPanelProps {
  clientId: string;
  sentListings: SentListing[];
  sentGuestCards: SentGuestCard[];
  invoices: Invoice[];
  isLoading?: boolean;
  onOpenListingEmail: (listing: SentListing) => void;
  onOpenGuestCardEmail: (guestCard: SentGuestCard) => void;
  onOpenInvoicePdf: (invoice: Invoice) => void;
}

const kindLabel: Record<FileItem["kind"], string> = {
  sent_list: "Sent List",
  guest_card: "Guest Card",
  invoice: "Invoice",
};

const kindIcon = (kind: FileItem["kind"]) => {
  if (kind === "invoice") return <PdfIcon fontSize="small" />;
  if (kind === "guest_card") return <EmailIcon fontSize="small" />;
  return <ArticleIcon fontSize="small" />;
};

export default function ClientFilesPanel({
  clientId,
  sentListings,
  sentGuestCards,
  invoices,
  isLoading,
  onOpenListingEmail,
  onOpenGuestCardEmail,
  onOpenInvoicePdf,
}: ClientFilesPanelProps) {
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  /**
   * The card HTML is already in memory — `email_html` is written on every
   * insert and DetailDrawer loads the row with select("*"). Re-render it into
   * a print window (browser print-to-PDF, same mechanism the dialog uses) with
   * no re-fetch.
   *
   * Fully synchronous: the popup is opened inside the click handler so it
   * inherits the user gesture and popup blockers leave it alone.
   */
  const handleDownloadGuestCard = (guestCard: SentGuestCard) => {
    setExportError(null);
    const html = guestCard.email_html;
    if (!html) {
      setExportError(
        "This guest card has no saved copy to download.",
      );
      return;
    }
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      setExportError(
        "The download was blocked by a popup blocker. Allow popups for this " +
          "site, then try again.",
      );
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const files = useMemo<FileItem[]>(() => {
    const listingItems: FileItem[] = sentListings.map((listing) => ({
      id: `sent-list-${listing.id}`,
      kind: "sent_list",
      label: listing.subject || "Sent apartment list",
      detail: `${listing.property_count || 0} properties · ${listing.unit_count || 0} units`,
      recipient: listing.recipient_email,
      createdAt: listing.sent_at,
      source: listing,
    }));

    const guestCardItems: FileItem[] = sentGuestCards.map((guestCard) => ({
      id: `guest-card-${guestCard.id}`,
      kind: "guest_card",
      label: guestCard.property_snapshot?.name || "Guest card",
      // Every row used to read "Guest card email", so a hand-submitted
      // download was filed as an email that never went out. `delivery_method`
      // has been written since migration 059 — read it.
      detail:
        guestCard.delivery_method === "downloaded"
          ? "Guest card · downloaded, no email sent"
          : "Guest card · emailed",
      recipient: guestCard.recipient_email,
      createdAt: guestCard.sent_at,
      source: guestCard,
    }));

    const invoiceItems: FileItem[] = invoices.map((invoice) => ({
      id: `invoice-${invoice.id}`,
      kind: "invoice",
      label: `Invoice ${invoice.invoice_number}`,
      detail: `${invoice.property_name} · $${Number(invoice.amount || 0).toLocaleString()} · ${invoice.status}`,
      recipient: invoice.recipient_email,
      createdAt: invoice.sent_at || invoice.created_at,
      source: invoice,
    }));

    return [...listingItems, ...guestCardItems, ...invoiceItems].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [sentListings, sentGuestCards, invoices]);

  const handleExportInvoices = async () => {
    setExportingCsv(true);
    setExportError(null);
    try {
      const blob = await exportInvoicesCsv({ clientId });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `locatenow-invoices-${clientId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      setExportError(error.message || "Could not export invoices");
    } finally {
      setExportingCsv(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (files.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          No files yet
        </Typography>
        <Typography variant="caption">
          Sent lists, guest cards, invoices, PDFs, emails, and uploads will collect here.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={0.85}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 800, textTransform: "uppercase" }}
        >
          Files ({files.length})
        </Typography>
        {invoices.length > 0 && (
          <Button
            size="small"
            variant="outlined"
            startIcon={exportingCsv ? <CircularProgress size={14} /> : <DownloadIcon />}
            onClick={handleExportInvoices}
            disabled={exportingCsv}
            sx={{ minHeight: 30 }}
          >
            CSV
          </Button>
        )}
      </Stack>
      {exportError && (
        <Typography variant="caption" color="error">
          {exportError}
        </Typography>
      )}
      {files.map((file) => (
        <Paper
          key={file.id}
          variant="outlined"
          sx={{
            p: 1.15,
            borderRadius: 1.5,
            backgroundColor: "rgba(255,255,255,0.015)",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center" }}>
              {kindIcon(file.kind)}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap" }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 650,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.label}
                </Typography>
                <Chip label={kindLabel[file.kind]} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
              </Stack>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.25, flexWrap: "wrap" }}>
                <Typography variant="caption" color="text.secondary">
                  {file.detail}
                </Typography>
                {file.recipient && (
                  <Typography variant="caption" color="text.secondary">
                    · {file.recipient}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  · {formatDate(file.createdAt)}
                </Typography>
              </Stack>
            </Box>
            {file.kind === "invoice" ? (
              <Tooltip title="Preview PDF">
                <IconButton
                  size="small"
                  onClick={() => onOpenInvoicePdf(file.source)}
                  sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.25 }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : file.kind === "guest_card" ? (
              <>
                <Tooltip title="Preview guest card">
                  <IconButton
                    size="small"
                    onClick={() => onOpenGuestCardEmail(file.source)}
                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.25 }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download guest card">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Download guest card"
                      disabled={!file.source.email_html}
                      onClick={() => handleDownloadGuestCard(file.source)}
                      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.25 }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="Preview email">
                <IconButton
                  size="small"
                  onClick={() => onOpenListingEmail(file.source)}
                  sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.25 }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}
